import { acquireTokenAsync } from '../AuthService';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';

type TQueryData = { [key: string]: string };

class WebAPI {
  private url: string;
  private version: string;

  constructor(url: string, version: string) {
    this.url = url;
    this.version = version;
  }

  async get(path: string, queryData?: TQueryData, isLocalStored = false): Promise<any> {
    if (isLocalStored) {
      return this.getFromLocalStorage(path);
    }

    const completeUrl = this.buildRequestUrl(this.url, this.version, path, queryData);
    const requestConfig = await this.createRequestConfig();
    return await this.requestWithTokenRefresh(
      () => axios.get(completeUrl, requestConfig),
      () => this.getWithFreshToken(completeUrl),
    );
  }

  getFromLocalStorage(path: string): any {
    const item = localStorage.getItem(path);

    return {
      data: item ? JSON.parse(item) : item,
    };
  }

  async post(path: string, data: any, isLocalStored = false, additionalHeaders = {}): Promise<any> {
    if (isLocalStored) {
      return this.postToLocalStorage(path, data);
    }

    const completeUrl = this.buildRequestUrl(this.url, this.version, path);
    const requestConfig = await this.createRequestConfig({}, additionalHeaders);
    return await this.requestWithTokenRefresh(
      () => axios.post(completeUrl, data, requestConfig),
      () => this.postWithFreshToken(completeUrl, data, additionalHeaders),
    );
  }

  postToLocalStorage(path: string, data: any): any {
    if (!data.id) {
      localStorage.setItem(path, JSON.stringify(data));

      return data;
    }

    const items = this.getFromLocalStorage(path)?.data ?? [];
    localStorage.setItem(
      path,
      JSON.stringify([...items.filter((item) => item.id !== data.id), data]),
    );

    return { data };
  }

  async put(path: string, data: any): Promise<any> {
    const completeUrl = this.buildRequestUrl(this.url, this.version, path);
    const requestConfig = await this.createRequestConfig();
    return await this.requestWithTokenRefresh(
      () => axios.put(completeUrl, data, requestConfig),
      () => this.putWithFreshToken(completeUrl, data),
    );
  }

  async patch(path: string, data: any): Promise<any> {
    const completeUrl = this.buildRequestUrl(this.url, this.version, path);
    const requestConfig = await this.createRequestConfig();
    return await this.requestWithTokenRefresh(
      () => axios.patch(completeUrl, data, requestConfig),
      () => this.patchWithFreshToken(completeUrl, data),
    );
  }

  async delete(path: string): Promise<any> {
    const completeUrl = this.buildRequestUrl(this.url, this.version, path);
    const requestConfig = await this.createRequestConfig();
    return await this.requestWithTokenRefresh(
      () => axios.delete(completeUrl, requestConfig),
      () => this.deleteWithFreshToken(completeUrl),
    );
  }

  async getAuthToken(forceRefresh = false): Promise<string> {
    const token = await acquireTokenAsync(forceRefresh);
    if (!token?.accessToken) {
      throw new Error('Authentication is continuing in a redirect.');
    }

    return token.accessToken;
  }

  async createRequestConfig(data: any = {}, additionalHeaders = {}): Promise<AxiosRequestConfig> {
    const token = await this.getAuthToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        ...additionalHeaders,
      },
      ...data,
    };
  }

  async createForceRefreshRequestConfig(additionalHeaders = {}): Promise<AxiosRequestConfig> {
    const token = await this.getAuthToken(true);
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        ...additionalHeaders,
      },
    };
  }

  private async getWithFreshToken(url: string): Promise<any> {
    return await axios.get(url, await this.createForceRefreshRequestConfig());
  }

  private async postWithFreshToken(url: string, data: any, additionalHeaders = {}): Promise<any> {
    return await axios.post(
      url,
      data,
      await this.createForceRefreshRequestConfig(additionalHeaders),
    );
  }

  private async putWithFreshToken(url: string, data: any): Promise<any> {
    return await axios.put(url, data, await this.createForceRefreshRequestConfig());
  }

  private async patchWithFreshToken(url: string, data: any): Promise<any> {
    return await axios.patch(url, data, await this.createForceRefreshRequestConfig());
  }

  private async deleteWithFreshToken(url: string): Promise<any> {
    return await axios.delete(url, await this.createForceRefreshRequestConfig());
  }

  async requestWithTokenRefresh<T>(
    request: () => Promise<T>,
    retryWithFreshToken: () => Promise<T>,
  ): Promise<T> {
    try {
      return await request();
    } catch (error) {
      if (!this.shouldRetryWithFreshToken(error)) {
        throw error;
      }

      return await retryWithFreshToken();
    }
  }

  private shouldRetryWithFreshToken(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
      return false;
    }

    const status = (error as AxiosError).response?.status;
    return status === 401 || status === 403;
  }

  buildRequestUrl(
    baseUrl: string,
    version: string,
    resourceUri: string,
    queryData?: TQueryData,
  ): string {
    const requestUrl = new URL([baseUrl, version, resourceUri].filter(Boolean).join('/'));

    if (queryData) {
      Object.keys(queryData).forEach((key) => {
        requestUrl.searchParams.append(key, queryData[key]);
      });
    }

    return requestUrl.toString();
  }
}

export default WebAPI;
